import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';

import { selectChatState, selectUiState } from '../../../../store/app.selectors';

import { BaseComponent } from '../../../shared/components/base/base.component';
import { AlertModalComponent } from '../../../shared/components/alert/alert.component';
import { ImagesService } from '../../../shared/services/images.service';
import { uiInitialState } from '../../../shared/store/ui.reducer';
import { IChannel } from '../../interfaces/IChannel';
import { ChannelsService } from '../../services/channels.service';
import { MessagingStateService } from '../../services/state.service';
import {
  changeOPT,
  changeReadChannelStatus,
  leaveChannel,
  muteChannel,
  pinChannel,
  toggleSidebar,
} from '../../store/chat.actions';
import { AppStateWithChat, chatInitialState } from '../../store/chat.reducer';
import { ChannelEditFormComponent } from '../channel-edit-form/channel-edit-form.component';

@Component({
  selector: 'ngx-channel-info',
  templateUrl: './channel-info.component.html',
  styleUrls: ['./channel-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChannelInfoComponent extends BaseComponent implements OnInit {
  @Output() orderIdEvent = new EventEmitter<string>();

  private store = inject(Store<AppStateWithChat>);
  private stateService = inject(MessagingStateService);
  public imagesService = inject(ImagesService);
  public dialog = inject(MatDialog);
  private channelsService = inject(ChannelsService);

  ui = toSignal(this.store.select(selectUiState), { initialValue: uiInitialState });
  chat = toSignal(this.store.select(selectChatState), { initialValue: chatInitialState });

  selectedWorkSpace = computed(() => this.chat().selectedWorkSpace);
  showGoToOrderButton = computed(() => this.ui().showGoToOrderButton);

  selectedChannel = signal<IChannel | null>(null);
  optStatus = signal(false);
  isMuted = signal(false);
  orderId = signal<string | null>(null);
  sizeListMembers = signal('');

  constructor() {
    super();
    effect(() => {
      const channel = this.chat().selectedChannel;
      if (!channel) {
        return;
      }
      this.optStatus.set(channel.enabled ?? false);
      this.selectedChannel.set({ ...channel });
      this.isMuted.set(channel.isMuted ?? false);
      this.orderId.set((this.channelsService.getTagValueByParam(channel, 'orderId') as string) ?? null);
    });
  }

  ngOnInit() {
    this.setSizeListMembers();
  }

  toggleSidebar(active: boolean) {
    this.store.dispatch(toggleSidebar({ active }));
  }

  emitOrder(): void {
    this.stateService.saveState('outsideNavigation');
    this.store.dispatch(toggleSidebar({ active: false }));
    this.orderIdEvent.emit(this.orderId() ?? '');
  }

  optInOutState() {
    this.optStatus.update((value) => !value);
    this.store.dispatch(changeOPT({ enabled: this.optStatus() }));
  }

  leaveChannel(): void {
    this.dialog
      .open(AlertModalComponent, {
        width: '300px',
        data: {
          title: 'Leave channel',
          message: 'Are you sure to leave this channel?',
        },
      })
      .afterClosed()
      .subscribe((res: boolean) => {
        if (res) {
          this.store.dispatch(leaveChannel({ channelUID: this.selectedChannel()!.uid }));
        }
      });
  }

  muteChannel(): void {
    this.isMuted.update((value) => !value);
    this.store.dispatch(muteChannel({ channel: this.selectedChannel()!, mute: this.isMuted() }));
  }

  pinChat(): void {
    const channel = this.selectedChannel();
    if (!channel) {
      return;
    }
    this.store.dispatch(pinChannel({ channel, pinned: !channel.pinned }));
  }

  setSizeListMembers(): void {
    const heightFirstContent = document.getElementById('first-content')?.offsetHeight || 0;
    this.sizeListMembers.set(`calc(100% - ${heightFirstContent + 53}px)`);
  }

  calculateContainerMembersHeight(): string {
    const heightFirstContent = document.getElementById('first-content')?.offsetHeight || 0;
    return `calc(100% - ${heightFirstContent}px)`;
  }

  editChannel(): void {
    this.dialog.open(ChannelEditFormComponent, {
      width: '500px',
      panelClass: 'custom-close-button-modal',
      data: {
        channel: this.selectedChannel(),
      },
    });
  }

  setAsRead(channel: IChannel, value: boolean): void {
    this.store.dispatch(changeReadChannelStatus({ channel, value }));
  }
}
